import { useCallback, useMemo, useState } from 'react'

import type {
  GridSelection,
  Row,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import {
  fromCellKey,
  getColumnCellCoords,
  getRowCellCoords,
  toCellKey,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'

interface UseGridSelectionProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  rowIndexByEntityId: Map<string, number>
}

function useGridSelection<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  rows,
  slots,
  rowIndexByEntityId,
}: UseGridSelectionProps<TEntity, TEvent>): {
  selection: GridSelection
  selectedCellKeys: Set<string>
  selectedRowId: string | null
  selectedColumnIndex: number | null
  selectedFocusedCell: { entityId: string; slotIndex: number } | null
  selectedCardSlotKeys: Set<string>
  clearSelection: () => void
  handleCellSelected: (
    entity: TEntity,
    slotIndex: number,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  handleRowHeaderSelected: (entity: TEntity) => void
  handleColumnHeaderSelected: (columnIndex: number) => void
  handleCardSlotSelected: (entityId: string, eventId: string) => void
  fromCellKey: (key: string) => { entityId: string; slotIndex: number }
} {
  const [selection, setSelection] = useState<GridSelection>({ kind: 'none' })

  const selectedCellKeys = useMemo(() => {
    return selection.kind === 'cells' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const selectedRowId = selection.kind === 'row' ? selection.rowId : null
  const selectedColumnIndex = selection.kind === 'column' ? selection.columnIndex : null

  const selectedFocusedCell = useMemo(() => {
    if (selection.kind !== 'cells') return null
    return selection.focused
  }, [selection])

  const clearSelection = useCallback(() => {
    setSelection({ kind: 'none' })
  }, [])

  const selectColumn = useCallback((columnIndex: number) => {
    setSelection({ kind: 'column', columnIndex })
  }, [])

  const selectColumnCellsExcept = useCallback(
    (columnIndex: number, excludedRowId: string) => {
      const cells = getColumnCellCoords(columnIndex, rows).filter(
        (cell) => cell.entityId !== excludedRowId
      )
      const next = new Set(cells.map((c) => toCellKey(c.entityId, c.slotIndex)))

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackRow = rows[0]?.entity.id
      const focusedRowId =
        fallbackRow && fallbackRow !== excludedRowId ? fallbackRow : rows[1]?.entity.id
      const firstSelectedKey = Array.from(next)[0]
      if (!firstSelectedKey) {
        setSelection({ kind: 'none' })
        return
      }
      const focusedKey = focusedRowId ? toCellKey(focusedRowId, columnIndex) : firstSelectedKey
      const focused = fromCellKey(focusedKey)
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [rows]
  )

  const selectSingleCell = useCallback((cell: { entityId: string; slotIndex: number }) => {
    const key = toCellKey(cell.entityId, cell.slotIndex)
    setSelection({ kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell })
  }, [])

  const toggleCell = useCallback((cell: { entityId: string; slotIndex: number }) => {
    const key = toCellKey(cell.entityId, cell.slotIndex)
    setSelection((prev) => {
      if (prev.kind !== 'cells') {
        return { kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell }
      }

      const next = new Set(prev.keys)
      const wasSelected = next.has(key)
      // Meta-click toggles the clicked cell. If it was already selected, remove it.
      if (wasSelected) {
        next.delete(key)
      } else {
        next.add(key)
      }

      if (next.size === 0) {
        return { kind: 'none' }
      }
      const focused = cell
      let anchor = prev.anchor

      const anchorKey = toCellKey(anchor.entityId, anchor.slotIndex)

      // Keep anchor inside the selection; clipboard offsets rely on this invariant.
      if (!next.has(anchorKey)) {
        const focusedKey = toCellKey(focused.entityId, focused.slotIndex)

        const fallbackKey = next.has(focusedKey) ? focusedKey : Array.from(next)[0]

        if (!fallbackKey) {
          // should be unreachable because keys.size > 0
          return { kind: 'none' }
        }
        anchor = fromCellKey(fallbackKey)
      }

      return {
        kind: 'cells',
        keys: next,
        anchor,
        focused,
      }
    })
  }, [])

  const addRangeSameRow = useCallback(
    (rowId: string, startIndex: number, endIndex: number, append: boolean, focusedSlot: number) => {
      const minIndex = Math.min(startIndex, endIndex)
      const maxIndex = Math.max(startIndex, endIndex)
      setSelection((prev) => {
        const next = append && prev.kind === 'cells' ? new Set(prev.keys) : new Set<string>()
        for (let i = minIndex; i <= maxIndex; i += 1) {
          next.add(toCellKey(rowId, i))
        }
        const focused = { entityId: rowId, slotIndex: focusedSlot }
        const anchor = prev.kind === 'cells' ? prev.anchor : focused
        return { kind: 'cells', keys: next, anchor, focused }
      })
    },
    []
  )

  const addRangeSameColumn = useCallback(
    (
      columnIndex: number,
      startRowId: string,
      endRowId: string,
      append: boolean,
      focusedRowId: string
    ) => {
      const startIndex = rowIndexByEntityId.get(startRowId)
      const endIndex = rowIndexByEntityId.get(endRowId)
      if (startIndex === undefined || endIndex === undefined) return

      const minIndex = Math.min(startIndex, endIndex)
      const maxIndex = Math.max(startIndex, endIndex)
      setSelection((prev) => {
        const next = append && prev.kind === 'cells' ? new Set(prev.keys) : new Set<string>()
        for (let i = minIndex; i <= maxIndex; i += 1) {
          const row = rows[i]
          if (!row) continue
          next.add(toCellKey(row.entity.id, columnIndex))
        }
        const focused = { entityId: focusedRowId, slotIndex: columnIndex }
        const anchor = prev.kind === 'cells' ? prev.anchor : focused
        return { kind: 'cells', keys: next, anchor, focused }
      })
    },
    [rowIndexByEntityId, rows]
  )

  const selectRowCellsExcept = useCallback(
    (rowId: string, excludedSlotIndex: number) => {
      const cells = getRowCellCoords(rowId, slots.length).filter(
        (cell) => cell.slotIndex !== excludedSlotIndex
      )
      const next = new Set(cells.map((c) => toCellKey(c.entityId, c.slotIndex)))

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackSlot = excludedSlotIndex === 0 ? 1 : 0
      const firstSelectedKey = Array.from(next)[0]
      if (!firstSelectedKey) {
        setSelection({ kind: 'none' })
        return
      }
      const focusedKey = next.has(toCellKey(rowId, fallbackSlot))
        ? toCellKey(rowId, fallbackSlot)
        : firstSelectedKey
      const focused = fromCellKey(focusedKey)
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [slots.length]
  )

  const handleCellSelected = useCallback(
    (
      entity: TEntity,
      slotIndex: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      const cell = { entityId: entity.id, slotIndex }
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey
      const anchorCell = selection.kind === 'cells' ? selection.anchor : null

      if (modifiers.shiftKey && !anchorCell) {
        selectSingleCell(cell)
        return
      }

      if (modifiers.shiftKey && anchorCell) {
        const sameRow = anchorCell.entityId === entity.id
        const sameColumn = anchorCell.slotIndex === slotIndex
        if (sameRow) {
          addRangeSameRow(entity.id, anchorCell.slotIndex, slotIndex, isMetaPressed, slotIndex)
          return
        }
        if (sameColumn) {
          addRangeSameColumn(slotIndex, anchorCell.entityId, entity.id, isMetaPressed, entity.id)
          return
        }
        selectSingleCell(cell)
        return
      }

      if (isMetaPressed && selectedRowId === entity.id) {
        selectRowCellsExcept(entity.id, slotIndex)
        return
      }

      if (isMetaPressed && selectedColumnIndex === slotIndex) {
        selectColumnCellsExcept(slotIndex, entity.id)
        return
      }

      if (isMetaPressed) {
        toggleCell(cell)
        return
      }

      if (selection.kind === 'cells' && selection.keys.size === 1) {
        const key = toCellKey(entity.id, slotIndex)
        if (selection.keys.has(key)) {
          clearSelection()
          return
        }
      }

      selectSingleCell(cell)
    },
    [
      addRangeSameColumn,
      addRangeSameRow,
      clearSelection,
      selectSingleCell,
      selection,
      selectedColumnIndex,
      selectedRowId,
      selectRowCellsExcept,
      selectColumnCellsExcept,
      toggleCell,
    ]
  )

  const handleRowHeaderSelected = useCallback(
    (entity: TEntity) => {
      if (selectedRowId === entity.id) {
        clearSelection()
        return
      }
      setSelection({ kind: 'row', rowId: entity.id })
    },
    [clearSelection, selectedRowId]
  )

  const handleColumnHeaderSelected = useCallback(
    (columnIndex: number) => {
      if (selectedColumnIndex === columnIndex) {
        clearSelection()
        return
      }
      selectColumn(columnIndex)
    },
    [clearSelection, selectColumn, selectedColumnIndex]
  )

  const toCardSlotKey = useCallback((entityId: string, eventId: string) => {
    return `${entityId}:${eventId}`
  }, [])

  const selectedCardSlotKeys = useMemo(() => {
    return selection.kind === 'cardSlots' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const handleCardSlotSelected = useCallback(
    (entityId: string, eventId: string) => {
      const key = toCardSlotKey(entityId, eventId)
      setSelection((prev) => {
        if (prev.kind !== 'cardSlots') {
          return { kind: 'cardSlots', keys: new Set([key]) }
        }

        const next = new Set(prev.keys)
        if (next.has(key)) {
          next.delete(key)
        } else {
          next.add(key)
        }

        if (next.size === 0) {
          return { kind: 'none' }
        }

        return { kind: 'cardSlots', keys: next }
      })
    },
    [toCardSlotKey]
  )

  return {
    selection,
    selectedCellKeys,
    selectedRowId,
    selectedColumnIndex,
    selectedFocusedCell,
    selectedCardSlotKeys,
    clearSelection,
    handleCellSelected,
    handleRowHeaderSelected,
    handleColumnHeaderSelected,
    handleCardSlotSelected,
    fromCellKey,
  }
}

export { useGridSelection }
